import base from "../middlewares/interceptors";
import {
  getPaginatedGroupsListUrl,
  getUserGroupUrl,
  createGroupUrl,
  updateGroupUrl,
  deleteGroupUrl,
  getUserGroupUsersUrl,
  getUserGroupRolesUrl,
  getUserGroupAuthTagsUrl,
  apiBaseUrlConfig,
  assignUserToGroupUrl,
} from "./constants";

// Get paginated groups list
export const getPaginatedUserGroupList = async ({
  page,
  pSize,
  searchTerm,
  userGroupFilterIds,
  signal,
  isActive = "true",
}: any) => {
  try {
    const params = new URLSearchParams();
    params.append("page", page);
    params.append("size", pSize);

    // Add user_group_filter_ids if provided (comma-separated IDs)
    if (userGroupFilterIds) {
      params.append("user_group_filter_ids", userGroupFilterIds);
    }

    // Add name filter if searchTerm is provided and has minimum 3 characters
    if (searchTerm && searchTerm.trim().length >= 3) {
      params.append("name", searchTerm.trim());
    }

    // Add is_active parameter if not "all"
    if (isActive !== "all") {
      const isActiveBoolean = isActive === "true" ? "true" : "false";
      params.append("is_active", isActiveBoolean);
    }

    const response = await base.get(
      `${getPaginatedGroupsListUrl}?${params.toString()}`,
      {
        signal, // Add abort signal for request cancellation
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching paginated groups list:", error);
    throw error;
  }
};

// Get group by ID
export const getUserGroupById = async (id: string | number) => {
  try {
    const response = await base.get(`${getUserGroupUrl}/${id}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching group by ID:", error);
    throw error;
  }
};

// Create new group
export const createUserGroup = async (payload: {
  name: string;
  description: string;
}) => {
  try {
    const response = await base.post(createGroupUrl, payload);
    return response.data;
  } catch (error) {
    console.error("Error creating group:", error);
    throw error;
  }
};

// Update group
export const updateUserGroup = async (
  id: string | number,
  payload: { name: string; description: string }
) => {
  try {
    const response = await base.put(`${updateGroupUrl}/${id}`, payload);
    return response.data;
  } catch (error) {
    console.error("Error updating group:", error);
    throw error;
  }
};

// Delete group
export const deleteUserGroup = async (id: string | number) => {
  try {
    const response = await base.delete(`${deleteGroupUrl}/${id}`);
    return response.data;
  } catch (error) {
    console.error("Error deleting group:", error);
    throw error;
  }
};

// Reactivate group
export const reactivateGroup = async (id: string | number) => {
  try {
    const response = await base.put(`${getUserGroupUrl}/${id}/reactivate`, {});
    return response.data;
  } catch (error) {
    console.error("Error reactivating group:", error);
    throw error;
  }
};

// Get group roles
export const getGroupRoles = async (groupId: string | number) => {
  try {
    const response = await base.get(
      `${getUserGroupRolesUrl}/${groupId}/roles?include_inactive=false&page=1&size=25`
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching group roles:", error);
    throw error;
  }
};

// Get group tags
export const getGroupAuthTags = async (groupId: string | number) => {
  try {
    const response = await base.get(
      `${getUserGroupAuthTagsUrl}/${groupId}/auth-tags?include_inactive=false&page=1&size=25`
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching group tags:", error);
    throw error;
  }
};

// Assign users to group
export const assignUserToGroup = async (payload: {
  group_id: number;
  user_id: number;
}) => {
  try {
    const response = await base.post(`${assignUserToGroupUrl}`, payload);
    return response.data;
  } catch (error) {
    console.error("Error assigning user to group:", error);
    throw error;
  }
};

// Get all groups
export const getAllUserGroups = async () => {
  try {
    const response = await base.get(`${getUserGroupUrl}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching all groups:", error);
    throw error;
  }
};

// Get groups by user ID
export const getGroupsByUserId = async (userId: string | number) => {
  try {
    const response = await base.get(
      `${getUserGroupUsersUrl}/user/${userId}/groups?include_inactive=false`
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching groups by user ID:", error);
    throw error;
  }
};

// Group Assignment Update Operations
export const updateGroupRoles = async (
  groupId: string | number,
  roleIds: number[]
) => {
  try {
    const payload = {
      user_group_id: Number(groupId),
      role_ids: roleIds,
    };
    const response = await base.post(
      `${apiBaseUrlConfig}/user-group-roles/update-roles`,
      payload
    );
    return response?.data;
  } catch (error) {
    console.error("Error updating group roles:", error);
    throw error;
  }
};

export const updateGroupAuthTags = async (
  groupId: string | number,
  authTagIds: number[]
) => {
  try {
    const payload = {
      user_group_id: Number(groupId),
      auth_tag_ids: authTagIds,
    };
    const response = await base.post(
      `${apiBaseUrlConfig}/user-group-auth-tags/update-auth-tags`,
      payload
    );
    return response?.data;
  } catch (error) {
    console.error("Error updating group tags:", error);
    throw error;
  }
};
